/**
 * Initials on a colour derived from the contact.
 *
 * Two things were wrong with the previous version, and both showed up in the
 * screenshots rather than in any assertion.
 *
 * It produced `oklch(0.88 0.05 hue)` — a *light* circle, computed the same way
 * whatever the theme, so in the dark theme every avatar was a pale disc glowing
 * on a near-black ground. Colour that ignores the theme is the one kind that
 * cannot be fixed by redefining a token.
 *
 * And 0.05 chroma is barely colour at all. Across a list the contacts read as one
 * washed sage repeated, where a populated inbox should look populated.
 *
 * Now it picks from eight curated tints defined per theme, so a dark avatar is a
 * deep colour carrying a light monogram — the inverse relationship, not the same
 * one dimmed.
 */
export function Avatar({ name, waId, size = 40 }: { name: string | null; waId: string; size?: number }) {
  const label = (name ?? waId).trim();
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase() || '#';

  // Hash the wa_id, not the name: a contact who supplies a profile name later
  // must not change colour in the list.
  let hash = 0;
  for (let i = 0; i < waId.length; i += 1) hash = (hash * 31 + waId.charCodeAt(i)) | 0;
  const tint = (Math.abs(hash) % 8) + 1;

  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full font-semibold select-none"
      style={{
        width: size,
        height: size,
        // Tracks the monogram rather than the circle: at 40px this is 15px, which
        // sits on the type scale rather than between two of its steps.
        fontSize: Math.round(size * 0.38),
        letterSpacing: '0.01em',
        background: `var(--color-tint-${tint})`,
        color: `var(--color-tint-ink-${tint})`,
      }}
    >
      {initials}
    </span>
  );
}
